import { parse, ParsedDomain, ParseError } from 'psl';
import { StringSchema } from 'yup';

export class InvalidDomainError extends Error {
  public override name = 'InvalidDomainError';

  constructor(
    message: string,
    public code:
      | ParseError['error']['code']
      | 'TLD_NOT_LISTED'
      | 'DOMAIN_IS_EMPTY'
      | 'TLD_IS_EMPTY'
      | 'SUBDOMAIN_NOT_ALLOWED'
  ) {
    super(`Domain is invalid: ${message}`);
  }
}

export const isValidDomain = (
  domain: string | undefined | null,
  { excludeDomainsWithSubdomains = true } = {}
): boolean => {
  if (!domain) {
    return false;
  }

  try {
    const { subdomain } = parseDomain(domain, { excludeDomainsWithSubdomains });
    if (excludeDomainsWithSubdomains && subdomain) {
      return false;
    }
  } catch (error) {
    if (error instanceof InvalidDomainError) {
      return false;
    } else {
      throw error;
    }
  }

  return true;
};

export const parseDomain = (
  domain: string | undefined | null,
  { excludeDomainsWithSubdomains = true } = {}
): ParsedDomain => {
  if (!domain) {
    throw new InvalidDomainError('Domain is empty', 'DOMAIN_IS_EMPTY');
  }

  const parsedDomainOrError = parse(domain);

  if (parsedDomainOrError.error) {
    throw new InvalidDomainError(
      parsedDomainOrError.error.message,
      parsedDomainOrError.error.code
    );
  }

  // TODO: Figure out why "as ParsedDomain" is needed here (for some reason, the pdf-service tests fail without it)
  const parsedDomain = parsedDomainOrError as ParsedDomain;

  if (excludeDomainsWithSubdomains && parsedDomain.subdomain) {
    throw new InvalidDomainError(
      'Domain contains subdomain',
      'SUBDOMAIN_NOT_ALLOWED'
    );
  }

  if (!parsedDomain.tld) {
    throw new InvalidDomainError('Top level domain is empty', 'TLD_IS_EMPTY');
  }

  if (!parsedDomain.listed) {
    throw new InvalidDomainError(
      `Top level domain is unrecognized: ${parsedDomain.tld}`,
      'TLD_NOT_LISTED'
    );
  }

  return parsedDomain;
};

export const validateYupDomain = (yupString: StringSchema) =>
  yupString
    .transform((val) => {
      try {
        return parseDomain(val).domain;
      } catch (cause) {
        return val;
      }
    })
    .test({
      name: 'domain',
      test: (val, ctx) => {
        try {
          parseDomain(val);
          return true;
        } catch (cause) {
          if (cause instanceof InvalidDomainError) {
            return ctx.createError({
              message: cause.message,
              path: ctx.path,
            });
          } else {
            return ctx.createError({
              message: `Unknown error: ${
                cause instanceof Error ? cause.message : cause
              }`,
              path: ctx.path,
            });
          }
        }
      },
    });
